import React from "react";
import { useTranslation } from "react-i18next";
import {
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Badge,
} from "reactstrap";

import { LocalizedProperty } from "../../interfaces";
import { MediaEducationalPathDto } from "../../interfaces/media";
import { useGetMediaEducationalPathQuery } from "../../store/api/mediaApi";

type TProps = {
  mediaId?: string | null;
  mediaName?: string | null;
  show: boolean;
  onCloseClick: () => void;
};

const PathField: React.FC<{
  label: string;
  value?: LocalizedProperty;
  lang: "en" | "ar";
}> = ({ label, value, lang }) => (
  <div className="mb-2">
    <Label className="fw-bold mb-0 text-muted fs-12">{label}</Label>
    <div>{value?.[lang] || value?.en || "-"}</div>
  </div>
);

export const MediaEducationalPathModal: React.FC<TProps> = ({
  mediaId,
  mediaName,
  show,
  onCloseClick,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "ar" ? "ar" : "en";

  const { data, isLoading, isError } = useGetMediaEducationalPathQuery(
    mediaId as string,
    { skip: !show || !mediaId },
  );
  const paths = Array.isArray(data?.paths) ? data.paths : [];

  return (
    <Modal size="lg" isOpen={show} toggle={onCloseClick} centered>
      <ModalHeader toggle={onCloseClick}>
        {t("educationalPath")}
        {mediaName ? (
          <span className="text-muted fs-13 ms-2">— {mediaName}</span>
        ) : null}
      </ModalHeader>
      <ModalBody>
        {isLoading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : isError ? (
          <p className="text-danger mb-0">{t("error")}</p>
        ) : paths.length === 0 ? (
          <p className="text-muted mb-0">{t("noEducationalPath")}</p>
        ) : (
          <div className="d-flex flex-column gap-3">
            {paths.map((path: MediaEducationalPathDto, index: number) => (
              <div
                key={`${path.lectureName?.en}-${path.gradeName?.en}-${index}`}
                className="border rounded p-3"
              >
                {paths.length > 1 && (
                  <Badge color="secondary" className="mb-2">
                    {t("path")} {index + 1}
                  </Badge>
                )}
                <div className="row g-2">
                  <div className="col-md-6">
                    <PathField
                      label={t("educationSystem")}
                      value={path.educationSystemName}
                      lang={lang}
                    />
                  </div>
                  <div className="col-md-6">
                    <PathField
                      label={t("curriculum")}
                      value={path.curriculumName}
                      lang={lang}
                    />
                  </div>
                  <div className="col-md-6">
                    <PathField
                      label={t("educationalStage")}
                      value={path.stageName}
                      lang={lang}
                    />
                  </div>
                  <div className="col-md-6">
                    <PathField
                      label={t("course")}
                      value={path.courseName}
                      lang={lang}
                    />
                  </div>
                  <div className="col-md-6">
                    <PathField
                      label={t("grade")}
                      value={path.gradeName}
                      lang={lang}
                    />
                  </div>
                  <div className="col-md-6">
                    <PathField
                      label={t("subject")}
                      value={path.subjectName}
                      lang={lang}
                    />
                  </div>
                  <div className="col-md-6">
                    <PathField
                      label={t("chapter")}
                      value={path.chapterName}
                      lang={lang}
                    />
                  </div>
                  <div className="col-md-6">
                    <PathField
                      label={t("lesson")}
                      value={path.lessonName}
                      lang={lang}
                    />
                  </div>
                  <div className="col-md-6">
                    <PathField
                      label={t("lecture")}
                      value={path.lectureName}
                      lang={lang}
                    />
                  </div>
                  <div className="col-md-6">
                    <div className="mb-2">
                      <Label className="fw-bold mb-0 text-muted fs-12">
                        {t("duration")}
                      </Label>
                      <div>{path.duration || "-"}</div>
                    </div>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-top">
                  <Label className="fw-bold mb-1 text-muted fs-12">
                    {t("appPath")}
                  </Label>
                  <code className="d-block small text-wrap">
                    {[
                      path.educationSystemName?.[lang],
                      path.curriculumName?.[lang],
                      path.stageName?.[lang],
                      path.courseName?.[lang],
                      path.gradeName?.[lang],
                      path.subjectName?.[lang],
                      path.chapterName?.[lang],
                      path.lessonName?.[lang],
                      path.lectureName?.[lang],
                    ]
                      .filter(Boolean)
                      .join(" → ")}
                  </code>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="d-flex justify-content-center mt-4 mb-1">
          <button
            type="button"
            className="btn w-sm btn-light"
            onClick={onCloseClick}
          >
            {t("close")}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};
